import {
    AccountAttrAlignment,
    AccountGridDataType,
    AccountGridGroup,
    DataDictionaryService,
} from '../../data/data-dictionary';

import {Aggregate, Control, DataType} from "@mescius/wijmo";
import {
    Column,
    ColumnGroup,
    ColumnGroupCollection,
    DataMap,
    DataMapEditor,
    FlexGrid,
    ICellTemplateFunction
} from "@mescius/wijmo.grid";
import {isArray} from 'es-toolkit/compat';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {ArrayUtils} from '../../lib/arrays';
import {Stdlib} from '../../lib/stdlib';
import {EmailAutoComplete} from '../../data/data-maps';

export type AppAccountColumnDeclaration = string | WijmoColumnDeclaration;


export interface WijmoColumnDeclaration {
    attribute?: string;
    header?: string,
    name?: string;
    visible?: boolean;
    collapseTo?: string[];
    isCollapsed?: boolean;
    align?: string;
    columns?: AppAccountColumnDeclaration[];
    binding?: string;
    editor?: Control | string;
    dataMap?: DataMap;
    format?: string;
    width?: number | string;
    minWidth?: number;
    dataType?: DataType;
    cssClass?: string;
    cssClassAll?: string;
    cellTemplate?: string | ICellTemplateFunction;
    isContentHtml?: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    aggregate?: Aggregate;
    wordWrap?: boolean;
    _freezeUpToHere?: boolean;
}

export interface ColumnWithMetdata extends Column {
    groupKey?: AccountGridGroup;
    groupName?: string;
    groupCssClass?: string;
}

const TieringCollapseTo = ['inceptionDate', 'expiryDate', 'gnwpEur', 'portfolioWeight'];
const TrackingCollapseTo = ['uwbRenewalId', 'renewalStatus', 'renewalGnwp', 'renewalInceptionDate', 'renewalExpiryDate', 'methodology', 'repricingFacilityErc', 'manualErc', 'manualOverride'];
const OutputCollapseTo = ['scoreAts', 'finalPilStatus', 'effectiveRateChange'];

const AccountGridStandardView: AppAccountColumnDeclaration[] =
    [
        {
            header:"Core Data", name: "CoreDataGroup", align: 'center',
            collapseTo: [ 'status', 'errorLevel', 'uwbId', 'account'],
            isCollapsed: false,
            columns: [
                { attribute: "row", visible: false },
                "status", "errorLevel",
                { attribute: "crmSubmissionId", visible: false },
                //{ attribute: "agoraId", visible: false },
                { attribute: "uwbId" }, // , _freezeUpToHere: true },
                { attribute: "masterPolicy", visible: false },
                { attribute: "policyId", visible: false },
                "uwy",
                //{ attribute: "techExId",  visible: false },
                "account", "broker",
                { attribute: "underwriter"/*, editor: "EmailAutoComplete"*/ }
            ]
        },
        {
            header:"Tiering", name: "TieringGroup", align: 'center',
            collapseTo: TieringCollapseTo,
            columns: [
                "product", "country", "inceptionDate", "expiryDate", "expiringCurrency",
                "gwp",
                "gwpEur",
                "gnwpEur", "prevPilStatus", "keyAccount",
                "renewableFlag", "targetRateChangeKeyAccounts", "retentionRateKeyAccounts",
                { header:"KPIs", name: "KpiGroup", align: 'center', columns: []},
                "multinationalFlag", "portfolioWeight", "bucketWeight", "customNotes", "manualPilStatus", "rationaleChange",
            ]},
        {
            header:"Tracking", name: "TrackingGroup", align: 'center',
            collapseTo: TrackingCollapseTo,
            columns: [
                {
                    header:"Renewal", align: 'center',
                    columns: [
                        "crmRenewalId", "uwbRenewalId",
                        { attribute: "renewalUnderwriter", visible: true },
                        "renewalStatus", "renewalGwp", "renewalGnwp", "renewalCurrency",
                        { attribute: "renewalGwpEur", visible: false },
                        { attribute: "renewalGnwpEur", visible: false },
                        "renewalInceptionDate", "renewalExpiryDate", "renewalRunningDays",
                        { attribute: "renewalAgoraStatus", visible: false },
                        { attribute: "renewalAgoraSubStatus", visible: true },
                    ]
                },
                "methodology",
                {
                    header:"T&C Comparison", align: 'center',
                    columns: [
                        {
                            header:"Expiring", name: "TrackingExpiringGroup", align: 'center',
                            columns: ["aptp", "expiringShare", "expiringExposure", "expiringDeductible", "expiringLimit",]
                        },
                        {
                            header:"Renewal", name: "TrackingRenewalGroup", align: 'center',
                            columns: [ "renewalAptp", "renewalShare", "renewalExposure", "renewalDeductible", "renewalLimit", ]
                        },
                    ]
                },
                "purePremiumChange",
                {
                    header: "Effective Rate Change", align: 'center',
                    columns: [
                        "repricingFacilityErc", "manualErc", "comments", "manualOverride",
                    ]
                },
            ]},
        {
            header:"Output Data", name: "OutputGroup", align: 'center',
            collapseTo: OutputCollapseTo,
            columns: [
                "scoreAts",
                "calculatedPilStatus",
                "finalPilStatus",
                "targetRateChange",
                "retainedGnwp",
                "riskFactors",
                "asIfPremiumExpired",
                "effectiveRateChange",
                "retentionRate"
            ]
        },
        {
            header:"Change History", name: "ChangeHistoryGroup", align: 'center',
            collapseTo: ["editedBy", "editedAt"],
            isCollapsed: true,
            columns: [ "editedBy", "editedAt", "version", "id" ]
        },
    ];

const AccountGridMinimalisticView: AppAccountColumnDeclaration[] =
    [
        { attribute: "row", visible: false },
        "status", "errorLevel",
        { attribute: "uwbId" },
        "account",
        { header:"KPIs", name: "KpiGroup", align: 'center', columns: []},
        {
            header:"Expiring", name: "TieringGroup", align: 'center',
            collapseTo: TieringCollapseTo,
            columns: [
                "gwp", "expiringCurrency", "expiryDate",
            ]},
        {
            header:"Renewal", name: "TrackingGroup", align: 'center',
            collapseTo: TrackingCollapseTo,
            columns: [
                "renewalStatus", "renewalGwp", "renewalCurrency",
                "renewalInceptionDate", "renewalExpiryDate"
            ]},
        {
            header:"Output Data", name: "OutputGroup", align: 'center',
            collapseTo: OutputCollapseTo,
            columns: [
                "finalPilStatus",
                "targetRateChange",
                "effectiveRateChange",
            ]
        }
    ];

const AccountGridDebugCompareView: AppAccountColumnDeclaration[] =
    [
        { attribute: "row", visible: false },
        "crmSubmissionId", "uwbId", "account",
        "gwp", "gwpEur", "gnwpEur",
        "scoreAts",
        "manualPilStatus", "calculatedPilStatus", "finalPilStatus",
        "targetRateChange", "effectiveRateChange", "manualErc",
    ];

const AccountGridTieringView: AppAccountColumnDeclaration[] =
    [
        {
            header:"Core Data", name: "CoreDataGroup", align: 'center',
            collapseTo: ['status', 'errorLevel', 'uwbId', 'account'],
            isCollapsed: true,
            columns: [
                { attribute: "row", visible: false },
                "status", "errorLevel",
                { attribute: "crmSubmissionId", visible: false },
                //{ attribute: "agoraId", visible: false },
                { attribute: "uwbId" }, // , _freezeUpToHere: true },
                { attribute: "masterPolicy", visible: false },
                { attribute: "policyId", visible: false },
                "uwy",
                //{ attribute: "techExId",  visible: false },
                "account", "broker",
                { attribute: "underwriter", /*editor: "EmailAutoComplete"*/ }
            ]
        },
        {
            header:"Tiering", name: "TieringGroup", align: 'center',
            collapseTo: TieringCollapseTo,
            columns: [
                "product", "country", "inceptionDate", "expiryDate", "expiringCurrency",
                "gwp",
                "gwpEur",
                "gnwpEur",
                "aptp", "expiringShare", "expiringExposure", "expiringDeductible", "expiringLimit",
                "prevPilStatus", "keyAccount",
                "renewableFlag", "targetRateChangeKeyAccounts", "retentionRateKeyAccounts",
                { header:"KPIs", name: "KpiGroup", align: 'center', columns: []},
                "multinationalFlag", "portfolioWeight", "bucketWeight", "customNotes", "manualPilStatus", "rationaleChange",
            ]},
        {
            header:"Output Data", name: "OutputGroup", align: 'center',
            collapseTo: OutputCollapseTo,
            columns: [
                "scoreAts",
                "calculatedPilStatus",
                "finalPilStatus",
                "targetRateChange",
                "retainedGnwp",
                "riskFactors",
                "asIfPremiumExpired",
                "retentionRate"
            ]
        },
        {
            header:"Change History", name: "ChangeHistoryGroup", align: 'center',
            collapseTo: ["editedBy", "editedAt"],
            isCollapsed: true,
            columns: [ "editedBy", "editedAt", "version", "id" ]
        },
    ]


export class AccountGridService {

    private static readonly STANDARD_VIEW_CONFIGURATION = AccountGridStandardView;

    /**
     * Maps data type to appropriate display format string
     */
    static getDisplayFormatForDataType(dataType: AccountGridDataType | undefined): string | undefined {
        if (!dataType) return undefined;
        switch (dataType) {
            case 'text':
                return '';
            case 'date':
                return 'dd/MM/yyyy';
            case 'datetime':
                return 'dd/MM/yyyy HH:mm:ss';
            case 'boolean':
                return '';
            case 'number':
                return 'n'
            case 'amount':
                return 'n0';
            case 'integer':
                return 'g0';
            case 'percentage':
                return 'p1';
            default:
                throw new Error(`Unknown data type: ${dataType}`);
        }
    }

    /**
     * Maps data type to appropriate column alignment
     */
    private static getAlignmentForDataType(dataType: AccountGridDataType | undefined): AccountAttrAlignment | undefined {
        if (!dataType) return undefined;
        switch (dataType) {
            case 'text': return 'left';
            case 'date': return 'center';
            case 'datetime': return 'center';
            case 'boolean': return 'center';
            case 'number': return 'right';
            case 'amount': return 'right';
            case 'integer': return 'center';
            case 'percentage': return 'right';
            default: throw new Error(`Unknown data type: ${dataType}`);
        }
    }

    /**
     * Maps data type to Wijmo DataType enum
     */
    private static getWijmoDataType(dataType: AccountGridDataType | undefined): DataType | undefined {
        if (!dataType) return undefined;
        switch (dataType) {
            case 'text': return DataType.String;
            case 'date': return DataType.Date;
            case 'datetime': return DataType.Date;
            case 'boolean': return DataType.Boolean;
            case 'number': return DataType.Number;
            case 'amount': return DataType.Number;
            case 'integer': return DataType.Number;
            case 'percentage': return DataType.Number;
            default: throw new Error(`Unknown data type: ${dataType}`);
        }
    }

    private static getDisplayNameForGroup(group?: AccountGridGroup): string {
        switch (group) {
            case 'core': return 'Core Data';
            case 'history': return 'Change History';
            case 'kpi': return 'KPI Fields';
            case 'output': return 'Output Data';
            case 'tracking-renewal': return 'Tracking - Renewal';
            case 'tracking-expiring': return 'Tracking - Expiring';
            case 'tiering': return 'Tiering';
            default: throw new Error(`Unknown group: ${group}`);
        }
    }

    /**
     * Converts application column declaration to Wijmo grid column declaration
     */
    private static convertToWijmoColumnDeclaration(columnDeclaration: AppAccountColumnDeclaration): WijmoColumnDeclaration {
        const normalizedDeclaration: AppAccountColumnDeclaration = typeof columnDeclaration === 'string'
            ? { attribute: columnDeclaration }
            : columnDeclaration;

        const dataAttribute = DataDictionaryService.getAccountDataAttribute(normalizedDeclaration.attribute);
        const isColumnGroup = isArray(normalizedDeclaration?.columns);

        // Default alignment depends on whether this is a column group or individual column
        const defaultAlignment = isColumnGroup
            ? 'center'
            : AccountGridService.getAlignmentForDataType(dataAttribute?.pfmDataType);

        const wijmoDeclaration: WijmoColumnDeclaration = {
            attribute: normalizedDeclaration.attribute,
            header: normalizedDeclaration.header ?? dataAttribute?.name,
            name: normalizedDeclaration.name,
            visible: normalizedDeclaration.visible,
            collapseTo: normalizedDeclaration.collapseTo ?? [],
            isCollapsed: normalizedDeclaration.isCollapsed,
            align: normalizedDeclaration.align ?? dataAttribute?.align ?? defaultAlignment,
            columns: normalizedDeclaration?.columns?.map(AccountGridService.convertToWijmoColumnDeclaration),
            binding: normalizedDeclaration.binding ?? normalizedDeclaration.attribute,
            editor: AccountGridService.lookupEditor(normalizedDeclaration.editor),
            dataMap: normalizedDeclaration.dataMap,
            format: normalizedDeclaration.format ?? dataAttribute?.format ?? AccountGridService.getDisplayFormatForDataType(dataAttribute?.pfmDataType),
            width: normalizedDeclaration.width ?? dataAttribute?.width,
            minWidth: normalizedDeclaration.minWidth,
            dataType: normalizedDeclaration.dataType ?? AccountGridService.getWijmoDataType(dataAttribute?.pfmDataType),
            cssClass: normalizedDeclaration.cssClass,
            cssClassAll: normalizedDeclaration.cssClassAll,
            cellTemplate: normalizedDeclaration.cellTemplate,
            isContentHtml: normalizedDeclaration.isContentHtml,
            isReadOnly: normalizedDeclaration.isReadOnly ?? dataAttribute?.isReadOnly ?? false,
            isRequired: normalizedDeclaration.isRequired ?? false,
            aggregate: normalizedDeclaration.aggregate,
            wordWrap: normalizedDeclaration.wordWrap,
            _freezeUpToHere: normalizedDeclaration._freezeUpToHere,
        };

        AccountGridService.cleanupColumnDeclaration(wijmoDeclaration);
        return wijmoDeclaration;
    }

    private static lookupEditor(editor: Control | string | undefined): Control | undefined {
        if (typeof editor === 'string') {
            switch (editor) {
                case 'EmailAutoComplete': return EmailAutoComplete;
                default: return undefined;
            }
        } else {
            return editor;
        }
    }

    /**
     * Converts array of application column declarations to Wijmo column declarations
     */
    private static convertColumnDeclarationsToWijmo(columnDeclarations: AppAccountColumnDeclaration[]): WijmoColumnDeclaration[] {
        return columnDeclarations.map(AccountGridService.convertToWijmoColumnDeclaration);
    }

    /**
     * Removes undefined properties and internal attributes from column declaration
     */
    private static cleanupColumnDeclaration(columnDeclaration: AppAccountColumnDeclaration): void {
        if (typeof columnDeclaration === 'string') return;

        // Remove internal properties that Wijmo won't accept
        // (note that Wijmo ignores all properties that start with an underscore)
        delete columnDeclaration.attribute;

        // Remove properties with undefined values (Wijmo will use default values)
        for (const [key, value] of Object.entries(columnDeclaration)) {
            if (value === undefined) {
                delete columnDeclaration[key as keyof AppAccountColumnDeclaration];
            }
        }

        // Recursively clean child columns
        columnDeclaration.columns?.forEach(AccountGridService.cleanupColumnDeclaration);
    }

    /**
     * Assigns group metadata to grid columns based on data dictionary
     */
    private static assignColumnGroupMetadata(grid: FlexGrid): void {
        const columnGroups = grid.columnGroups;
        grid.columns.forEach(column => {
            // try to assign the data-dictionary group (i.e. the business logic group)
            const dataAttribute = DataDictionaryService.getAccountDataAttribute(column.binding);
            const columnWithMetadata = column as ColumnWithMetdata;

            const group = dataAttribute?.group as AccountGridGroup;
            if (group) {
                columnWithMetadata.groupKey = group;
                columnWithMetadata.groupName = AccountGridService.getDisplayNameForGroup(group);
            }

            // check if this column is part of a Wijmo grid column group
            const columnGroup = AccountGridService.findParentColumnGroup(grid.getColumnGroups(), column);
            if (columnGroup?.name) {
                columnWithMetadata.groupCssClass = `group-${columnGroup.name.toLowerCase()}`;
            }
        });
    }

    private static findParentColumnGroup(columnGroups: ColumnGroupCollection, column: Column): ColumnGroup | undefined {
        for (const group of columnGroups) {
            if (group.columns) {
                if (group.columns.includes(column as ColumnGroup)) {
                    return group;
                } else {
                    const childMatch = AccountGridService.findParentColumnGroup(group.columns, column);
                    if (childMatch) return childMatch;
                }
            }
        }
        return undefined;
    }

    /**
     * Recursively searches for a column matching the given predicate.
     * Accepts both string column names and WijmoColumnDeclaration objects.
     * But never searches for string columns, only for object-like columns because
     * the predicate is applied to the object itself, not the string name.
     */
    private static findColumnRecursively(
        columns: AppAccountColumnDeclaration[],
        predicate: (col: WijmoColumnDeclaration) => boolean
    ): WijmoColumnDeclaration | undefined {
        for (const column of columns) {
            if (typeof column === 'string') continue;

            if (predicate(column)) {
                return column;
            }

            if (column.columns) {
                const childColumns = column.columns as WijmoColumnDeclaration[];
                const foundColumn = this.findColumnRecursively(childColumns, predicate);
                if (foundColumn) {
                    return foundColumn;
                }
            }
        }
        return undefined;
    }

    /**
     * Finds a column by its name in the column hierarchy
     */
    private static findColumnByName(columns: WijmoColumnDeclaration[], columnName: string): WijmoColumnDeclaration | undefined {
        return AccountGridService.findColumnRecursively(columns, column => column.name === columnName);
    }

    /**
     * Dynamically adds KPI columns to the KPI group based on portfolio segment data
     */
    static async populateKpiColumns(columns: WijmoColumnDeclaration[], portfolioSegmentData: PortfolioSegmentDataContext): Promise<void> {
        const kpiGroup = AccountGridService.findColumnByName(columns, 'KpiGroup');

        // Skip if KPI group not found or already populated
        if (!kpiGroup || ArrayUtils.length(kpiGroup.columns) > 0) {
            return;
        }

        const kpiData = await Stdlib.resourceDataToPromise(portfolioSegmentData.segmentKpis);
        const enrichedKpiDefinitions = kpiData.map(kpi => ({
            ...kpi,
            ...DataDictionaryService.KpiDefinitions.find(definition => definition.attribute === kpi.attribute)
        }));

        // apparently not sorted according to the position in the dictionary
        // const sortedKpiDefinitions = sortBy(enrichedKpiDefinitions, ['position']);
        const sortedKpiDefinitions = enrichedKpiDefinitions;

        const kpiColumnDeclarations: WijmoColumnDeclaration[] = sortedKpiDefinitions.map(kpi => ({
            ...AccountGridService.convertToWijmoColumnDeclaration(kpi.attribute),
            isReadOnly: false,
            cssClass: 'kpi-column'
        }));

        kpiGroup.columns = kpiColumnDeclarations;
    }

    /**
     * Assigns data maps to columns that require dropdown editors
     */
    private static async assignDataMapsToColumns(grid: FlexGrid, portfolioSegmentData: PortfolioSegmentDataContext): Promise<void> {
        for (const column of grid.columns) {
            const dataAttribute = DataDictionaryService.getAccountDataAttribute(column.binding);
            const dataMapKey = dataAttribute?.dataMapKey;

            if (dataMapKey) {
                column.dataMap = await portfolioSegmentData.getDataMap(dataMapKey);
                column.dataMapEditor = DataMapEditor.DropDownList;
            }
        }
    }

    /**
     * Returns the standard view configuration for account grid
     */
    public static getStandardViewConfiguration(): WijmoColumnDeclaration[] {
        return AccountGridService.convertColumnDeclarationsToWijmo(AccountGridService.STANDARD_VIEW_CONFIGURATION);
    }

    public static getMinimalisticViewConfiguration(): WijmoColumnDeclaration[] {
        return AccountGridService.convertColumnDeclarationsToWijmo(AccountGridMinimalisticView);
    }

    public static getTieringViewConfiguration(): WijmoColumnDeclaration[] {
        return AccountGridService.convertColumnDeclarationsToWijmo(AccountGridTieringView);
    }

    public static getDebugCompareViewConfiguration(): WijmoColumnDeclaration[] {
        return AccountGridService.convertColumnDeclarationsToWijmo(AccountGridDebugCompareView);
    }

    /**
     * Configures the grid with the specified columns and portfolio segment data
     */
    public static async configureGridColumns(
        grid: FlexGrid,
        columnDeclarations: AppAccountColumnDeclaration[],
        portfolioSegmentData?: PortfolioSegmentDataContext
    ): Promise<void> {
        const wijmoColumnGroups = AccountGridService.convertColumnDeclarationsToWijmo(columnDeclarations);

        if (portfolioSegmentData) {
            await AccountGridService.populateKpiColumns(wijmoColumnGroups, portfolioSegmentData);
        }

        grid.deferUpdate(async () => {
            // apply the columns groups and thus reset the grid completely
            // also unfreeze the columns - a later step will check if freezing is needed
            grid.columnGroups = wijmoColumnGroups;
            grid.frozenColumns = 0;
            AccountGridService.assignColumnGroupMetadata(grid);

            if (portfolioSegmentData) {
                await AccountGridService.assignDataMapsToColumns(grid, portfolioSegmentData);
            }
        });

        // Freeze columns when one of the columns has the attribute 'freezeUpToHere == true'
        // Auto-size column headers after a delay to ensure proper rendering
        setTimeout(() => {
            const lastFrozenColumn = AccountGridService.findColumnRecursively(columnDeclarations, d => d._freezeUpToHere ?? false);
            const lastFrozenColumnBinding = lastFrozenColumn?.binding ?? '__none__';
            const frozenColumnIndex = grid.columns.findIndex(col => col.binding === lastFrozenColumnBinding );

            // only freeze if the column is found - here we may run into a timing issue: this method can be called
            // be the apply 'favorites' function, which has frozen columns in a different logic,
            // and our setTimeout() callback must not revert what was done there
            if (frozenColumnIndex > 0) grid.frozenColumns = frozenColumnIndex + 1;

            // Auto-size column headers ... hope fully they are now visible to guarantee a correct size
            //grid.autoSizeRows(0, grid.columnHeaders.rows.length - 1, true)
            const columnHeaderRows = grid.columnHeaders.rows;
            columnHeaderRows[columnHeaderRows.length - 1].height = 80;
        }, 10);
    }
}
